import { useState } from 'react'
import { insightOpportunityCopy as copy } from '@testfeed/product-language'
import { MerchantFacingError } from '../../merchantErrors'
import type { InsightsLifecycle } from '../../pages/insights.page'
import type { TestInsightSetup } from './InsightSetupsSection'
import type { InsightConfirmationSession } from './useInsightOpportunityDetails'

/** The page owns single-flight, cancellation, diagnostics and progress across all card/modal copies. */
export function useInsightOpportunityAction(onTestSetup: TestInsightSetup, lifecycle: InsightsLifecycle) {
  const [batchId, setBatchId] = useState<string | null>(null)
  const pending = lifecycle.button('test-setup').loading ? batchId : null
  const feedback = lifecycle.feedback.find((item) => item.operation === 'test-setup')
  // The Worker's wording when the seed error carried it; otherwise this page's own seed-failed line — the same
  // product-language copy the non-saved outcome throws below. Insights is not a dumb-renderer surface (no
  // study-builder copy ratchet) and owns its loading/error/refresh/seed wording from product-language, so the
  // page-feedback fallback (removed) lands here at the one consumer rather than as a per-action declaration constant.
  const failure = feedback && batchId ? { batchId, message: feedback.message ?? copy.seedFailed } : null
  async function test(session: InsightConfirmationSession) {
    const result = await lifecycle.run('test-setup', async (signal) => {
      const { setup, confirmation } = session
      setBatchId(setup.batchId)
      // Assign before submission: even a lost POST response must retry this exact identity.
      // Only closing/reopening details gives the merchant a new confirmation session.
      if (!confirmation.current)
        confirmation.current = await onTestSetup.resolve(setup.batchId, setup.productId, signal)
      signal.throwIfAborted()
      const outcome = await onTestSetup.submit(confirmation.current, signal)
      if (outcome === 'scope_changed') throw new DOMException('Store scope changed', 'AbortError')
      if (outcome !== 'saved') throw new MerchantFacingError(copy.seedFailed)
      return outcome
    })
    if (result.status === 'done') return result.value
    if (result.status === 'cancelled') return 'scope_changed' as const
    return 'failed' as const
  }
  return { lifecycle, pending, failure, test }
}
